import { useCallback, useEffect, useRef, useState, type RefObject, type SetStateAction } from 'react';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import type { PixelPoint } from '@peruselab/structure-editor/domain/bonds';
import {
  DEFAULT_VIEWPORT,
  centerPageAxes,
  fitViewport,
  zoomViewportAt,
  type ViewportTransform
} from '@peruselab/structure-editor/domain/viewport';

export interface PanDrag {
  readonly pointerId: number;
  readonly startClientX: number;
  readonly startClientY: number;
  readonly startPanX: number;
  readonly startPanY: number;
}

export interface UsePaperViewportOptions {
  readonly atoms: readonly DrawingAtom[];
  readonly sizeRef: RefObject<PaperSize>;
  readonly documentId?: string | undefined;
  readonly isOversizedImport?: boolean;
}

/**
 * Viewport state and pan-gesture handling for DrawingPaper.tsx (item 33: see
 * docs/repo-topology-phase-5-drawing-paper-decomposition.md). Pan-gesture recognition is
 * absorbed here rather than left in pointer lifecycle, per the characterization's Coupling 1:
 * `beginPan`/`updatePan`/`endPan` give the pointer-handling code a narrow API instead of a raw
 * `setViewport` -- pointer lifecycle only needs to recognize *when* a pan gesture is happening
 * (which pointer, which tool/button), not how viewport state is mutated in response.
 */
export function usePaperViewport({ atoms, sizeRef, documentId, isOversizedImport = false }: UsePaperViewportOptions) {
  const [viewport, setViewport] = useState<ViewportTransform>(DEFAULT_VIEWPORT);
  // Zoom and Fit still allow page centring on resize; deliberate panning owns the view.
  const autoCenterPageRef = useRef(true);
  const navigateViewport = useCallback((next: SetStateAction<ViewportTransform>) => {
    autoCenterPageRef.current = false;
    setViewport(next);
  }, []);
  const [panDrag, setPanDrag] = useState<PanDrag | null>(null);
  // Mirrors `viewport` for beginPan's read of the live pan offset, the same pattern
  // usePaperRenderers uses for its own viewportRef -- keeps beginPan's identity stable across
  // pan ticks rather than depending on `viewport` itself.
  const viewportStateRef = useRef(viewport);
  useEffect(() => {
    viewportStateRef.current = viewport;
  }, [viewport]);

  const zoomBy = useCallback(
    (factor: number, screenPoint?: PixelPoint) => {
      const point = screenPoint ?? { x: sizeRef.current.width / 2, y: sizeRef.current.height / 2 };
      setViewport((current) => zoomViewportAt(current, current.zoom * factor, point));
    },
    [sizeRef]
  );

  const fitStructure = useCallback(() => {
    autoCenterPageRef.current = true;
    setViewport(
      fitViewport(
        atoms.map((atom) => atom.position),
        sizeRef.current
      )
    );
  }, [atoms, sizeRef]);

  // #772 -- fit any set of paper-normalized points (a page's corners, a drawing's or a selection's
  // complete visual extents); a no-op for nothing to fit. #807: returns the zoom it fitted to (null
  // for nothing), so the zoom menu can read "Fit" until the zoom changes again.
  const fitPoints = useCallback(
    (points: readonly { readonly x: number; readonly y: number }[]): number | null => {
      if (points.length === 0) return null;
      autoCenterPageRef.current = true;
      const next = fitViewport(points, sizeRef.current);
      setViewport(next);
      return next.zoom;
    },
    [sizeRef]
  );

  // #848 -- centre each axis that fits at 100%, even when the other axis is cropped.
  // On a cropped axis keep the visible centre fixed as before.
  const actualSize = useCallback(
    (page: { readonly x?: number; readonly y?: number; readonly width: number; readonly height: number }) => {
      const size = sizeRef.current;
      autoCenterPageRef.current = true;
      setViewport((current) =>
        centerPageAxes(zoomViewportAt(current, 1, { x: size.width / 2, y: size.height / 2 }), size, page)
      );
    },
    [sizeRef]
  );

  const resetView = useCallback(() => {
    autoCenterPageRef.current = true;
    setViewport(DEFAULT_VIEWPORT);
  }, []);

  // Whenever a new document is committed (new/reset/loaded/imported - graph.id is stable across
  // ordinary edits, only changing on replacement), reset the view: fit-to-structure for an
  // oversized import (see isOversizedImport), laid out at native bond length rather than shrunk
  // to fit, so the user isn't left at the default zoom/pan looking at whatever corner happens to
  // be top-left; otherwise back to the default view, so switching to a normal-sized document
  // doesn't inherit whatever pan/zoom was left over from a previous large one.
  useEffect(() => {
    autoCenterPageRef.current = true;
    if (isOversizedImport) fitStructure();
    else setViewport(DEFAULT_VIEWPORT);
    // Deliberately keyed on documentId/isOversizedImport only, not fitStructure itself (which
    // changes identity on every atoms recompute) - this should fire once per newly-committed
    // document, not on every render that happens to touch atoms.
  }, [documentId, isOversizedImport]);

  const beginPan = useCallback((pointerId: number, clientX: number, clientY: number) => {
    autoCenterPageRef.current = false;
    setPanDrag({
      pointerId,
      startClientX: clientX,
      startClientY: clientY,
      startPanX: viewportStateRef.current.panX,
      startPanY: viewportStateRef.current.panY
    });
  }, []);

  // Takes the in-progress drag explicitly rather than re-reading `panDrag` internally: the caller
  // already has it in scope (it gated the call on `panDrag?.pointerId === event.pointerId`), and
  // this keeps updatePan itself referentially stable across the drag.
  const updatePan = useCallback((drag: PanDrag, clientX: number, clientY: number) => {
    setViewport((current) => ({
      ...current,
      panX: drag.startPanX + clientX - drag.startClientX,
      panY: drag.startPanY + clientY - drag.startClientY
    }));
  }, []);

  const endPan = useCallback(() => {
    setPanDrag(null);
  }, []);

  return {
    viewport,
    setViewport: navigateViewport,
    setLayoutViewport: setViewport,
    autoCenterPageRef,
    panDrag,
    zoomBy,
    fitStructure,
    fitPoints,
    actualSize,
    resetView,
    beginPan,
    updatePan,
    endPan
  };
}
